/* TK: search drawer + recent searches + wider bag drawer.
   Loaded by snippets/search-dropdown.liquid (rendered in the header on every page).

   1. Search drawer. The theme still opens and closes the panel (component-search-dropdown.js: header SEARCH link,
      close button, Escape, click outside). tk-shop-blocks.css turns it into a right-hand drawer. This script:
        - moves the panel and its backdrop to the end of <body>, so the drawer and backdrop cover the header too
          (inside the header they could never rise above the header's own layer);
        - on open: draws the recent searches and moves focus into the search field;
        - on close: puts focus back on the SEARCH link that opened it;
        - closes it if another drawer (bag, size guide) opens.
   2. Recent searches: up to 5 terms in localStorage 'tk_recent_searches' (newest first, no duplicates in any case,
      no empty or 1-letter terms, trailing '*' dropped). Saved when a search is sent from the drawer or from any form
      whose action ends in /search, and when the /search page loads with a query. Shown as chips with a clock icon,
      before the trending chips.
   3. Bag drawer width: tk-shop-blocks.css widens it to the search drawer's width once this script has checked how the
      theme sizes it (see measureCartDrawer). */
(() => {
  if (window.tkSearchDrawer) return;
  window.tkSearchDrawer = true;

  const KEY = 'tk_recent_searches';
  const MAX = 5;

  /* ---------- Recent searches ---------- */
  const clean = (term) => String(term || '').replace(/\s+/g, ' ').trim().replace(/\*+$/, '').trim();

  const readRecent = () => {
    try {
      const list = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((t) => typeof t === 'string' && t.length > 1).slice(0, MAX) : [];
    } catch (e) {
      return [];
    }
  };

  const saveRecent = (term) => {
    const t = clean(term);
    if (t.length < 2) return;
    const lower = t.toLowerCase();
    const list = [t].concat(readRecent().filter((x) => x.toLowerCase() !== lower)).slice(0, MAX);
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) { /* storage blocked: nothing to do */ }
  };

  const isSearchPath = (path) => /\/search\/?$/.test(String(path || '').split(/[?#]/)[0]);

  // The /search page loaded with a query (covers the drawer's Enter key, suggestion links and the search page form)
  if (isSearchPath(window.location.pathname)) {
    try {
      const q = new URLSearchParams(window.location.search).get('q');
      if (q) saveRecent(q);
    } catch (e) { /* old browser */ }
  }

  // Any search form (header form on other templates, the search page's own form)
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !isSearchPath(form.getAttribute('action'))) return;
    const field = form.querySelector('[name="q"]');
    if (field) saveRecent(field.value);
  }, true);

  // The drawer has no form: the theme sends the search on Enter or the magnifier button
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target && e.target.matches && e.target.matches('search-dropdown [data-predictive-search-input]')) {
      saveRecent(e.target.value);
    }
  }, true);
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('search-dropdown [data-predictive-search-submit]');
    if (!btn) return;
    const field = btn.closest('predictive-search')?.querySelector('[data-predictive-search-input]');
    if (field) saveRecent(field.value);
  }, true);

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const clockIcon = () => {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('width', '14');
    svg.setAttribute('height', '14');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.4');
    const circle = document.createElementNS(SVG_NS, 'circle');
    circle.setAttribute('cx', '8');
    circle.setAttribute('cy', '8');
    circle.setAttribute('r', '6.5');
    const hands = document.createElementNS(SVG_NS, 'path');
    hands.setAttribute('d', 'M8 4.5V8l2.5 1.5');
    hands.setAttribute('stroke-linecap', 'round');
    svg.append(circle, hands);
    return svg;
  };

  const searchUrl = (panel) => {
    const base = panel.getAttribute('data-tk-search-url') || `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}search`;
    return (term) => `${base}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last&type=article%2Cproduct%2Cpage`;
  };

  const renderRecent = (panel) => {
    const row = panel.querySelector('[data-tk-chips]');
    if (!row) return;
    row.querySelectorAll('[data-tk-recent]').forEach((li) => li.remove());
    const link = searchUrl(panel);
    const frag = document.createDocumentFragment();
    readRecent().forEach((term) => {
      const li = document.createElement('li');
      li.setAttribute('data-tk-recent', '');
      const a = document.createElement('a');
      a.className = 'tk-chip tk-chip--recent';
      a.href = link(term);
      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      hidden.textContent = 'Recent search: ';
      const label = document.createElement('span');
      label.textContent = term;
      a.append(clockIcon(), hidden, label);
      li.append(a);
      frag.append(li);
    });
    row.prepend(frag);
  };

  /* ---------- Search drawer ---------- */
  let trigger = null;
  let skipFocusReturn = false;

  // Remember which SEARCH link opened the drawer (desktop and phone headers each have one)
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('.search-button');
    if (link) trigger = link;
  }, true);

  const visibleSearchLink = () => Array.from(document.querySelectorAll('.search-button')).find((el) => el.offsetParent !== null);

  const closeSearch = () => {
    if (window.eventBus) window.eventBus.emit('close:search:dropdown');
  };

  const setUp = (panel) => {
    if (panel.tkReady) return;
    panel.tkReady = true;

    // Move the drawer and its backdrop to the end of the page (out of the header's layer). The theme's components
    // re-attach their listeners when moved. In the theme editor a re-rendered header brings a fresh panel: drop the old one.
    const overlay = panel.nextElementSibling && panel.nextElementSibling.classList.contains('search-dropdown__overlay') ? panel.nextElementSibling : null;
    document.querySelectorAll('body > search-dropdown.tk-search').forEach((old) => {
      if (old === panel) return;
      const oldOverlay = old.nextElementSibling;
      if (oldOverlay && oldOverlay.classList.contains('search-dropdown__overlay')) oldOverlay.remove();
      old.remove();
    });
    if (panel.parentElement !== document.body) {
      document.body.append(panel);
      if (overlay) document.body.append(overlay);
    }

    if (overlay) {
      // Belt and braces: the theme already closes on any click outside the panel
      overlay.addEventListener('click', closeSearch);
    }

    panel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') closeSearch();
    });

    const input = panel.querySelector('[data-predictive-search-input]');
    renderRecent(panel);

    new MutationObserver(() => {
      const open = panel.getAttribute('aria-hidden') === 'false';
      if (open === panel.tkOpen) return;
      panel.tkOpen = open;
      if (open) {
        renderRecent(panel);
        if (input) {
          try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
        }
        document.querySelectorAll('.search-button').forEach((el) => el.setAttribute('aria-expanded', 'true'));
      } else {
        document.querySelectorAll('.search-button').forEach((el) => el.setAttribute('aria-expanded', 'false'));
        if (skipFocusReturn) {
          skipFocusReturn = false;
          return;
        }
        const back = (trigger && trigger.offsetParent !== null) ? trigger : visibleSearchLink();
        if (back) {
          try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); }
        }
      }
    }).observe(panel, { attributes: true, attributeFilter: ['aria-hidden'] });
    panel.tkOpen = panel.getAttribute('aria-hidden') === 'false';
  };

  // Another drawer opening (e.g. the bag after an add to bag) closes the search drawer; focus goes to that drawer
  const listenToDrawers = () => {
    if (!window.eventBus || listenToDrawers.done) return;
    listenToDrawers.done = true;
    window.eventBus.on('drawer:opened', () => {
      const panel = document.querySelector('search-dropdown.tk-search[aria-hidden="false"]');
      if (!panel) return;
      skipFocusReturn = true;
      closeSearch();
    });
  };

  /* ---------- Bag drawer width ----------
     The theme either sizes the <cart-drawer> element itself (and the backdrop sits outside it) or makes the element
     full screen and sizes only the inner panel. Widen whichever one it is; measured with neither class applied. */
  const measureCartDrawer = () => {
    const drawer = document.querySelector('cart-drawer.global-drawer');
    if (!drawer || drawer.classList.contains('tk-wide-self') || drawer.classList.contains('tk-wide-inner')) return;
    if (!window.matchMedia('(min-width: 768px)').matches) return; // phones: full width already (custom.css); check on desktop only
    const width = drawer.getBoundingClientRect().width;
    if (!width) return; // not laid out yet: try again when it opens
    const viewport = document.documentElement.clientWidth;
    drawer.classList.add(width >= viewport - 2 ? 'tk-wide-inner' : 'tk-wide-self');
  };

  const init = () => {
    document.querySelectorAll('search-dropdown.tk-search').forEach(setUp);
    listenToDrawers();
    measureCartDrawer();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('load', () => {
    listenToDrawers();
    measureCartDrawer();
    if (window.eventBus) window.eventBus.on('drawer:opened', measureCartDrawer);
  });
  window.addEventListener('resize', measureCartDrawer, { passive: true });
  // Theme editor: the header section re-renders with a new panel
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('search-dropdown.tk-search').forEach(setUp);
  });
})();
